import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Heart, ChevronLeft } from "lucide-react";
import { StarPicker } from "@/components/site/Stars";

const SWEETNESS = [
  { key: "too_sweet",         label: "Too sweet" },
  { key: "just_right",        label: "Just right" },
  { key: "not_sweet_enough",  label: "Not sweet enough" },
];
const TEXTURE = [
  { key: "too_soft",   label: "Too soft" },
  { key: "just_right", label: "Just right" },
  { key: "too_firm",   label: "Too firm" },
];
const REORDER = [
  { key: "definitely",    label: "Definitely" },
  { key: "maybe",         label: "Maybe" },
  { key: "probably_not",  label: "Probably not" },
];

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 rounded-full text-sm border transition-colors ${
        active ? "bg-espresso text-ivory border-espresso" : "border-line text-espresso hover:border-espresso"
      }`}
    >
      {children}
    </button>
  );
}

export default function ReviewForm() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");
  const [sweetness, setSweetness] = useState("");
  const [texture, setTexture] = useState("");
  const [reorder, setReorder] = useState("");
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/reviews/token/${token}`)
      .then(({ data }) => {
        setCtx(data);
        const first = data.items.find((i) => !i.already_reviewed);
        if (first) setSelectedProduct(first);
      })
      .catch((e) => setLoadErr(e?.response?.data?.detail || "This review link is no longer valid."));
  }, [token]);

  const resetForm = () => {
    setRating(0); setText(""); setSweetness(""); setTexture(""); setReorder("");
    setPhoto(null); setPhotoPreview(null); setDone(false);
  };

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("Photo must be under 10 MB"); return; }
    setPhoto(f);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!selectedProduct) return;
    if (rating < 1) { toast.error("Please choose a rating."); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("product_id", selectedProduct.product_id);
      fd.append("rating", String(rating));
      if (text.trim()) fd.append("text", text.trim());
      if (sweetness) fd.append("sweetness", sweetness);
      if (texture) fd.append("texture", texture);
      if (reorder) fd.append("would_order_again", reorder);
      if (photo) fd.append("photo", photo);
      await api.post(`/reviews/token/${token}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      // Refresh context so the finished product shows already_reviewed=true
      const { data } = await api.get(`/reviews/token/${token}`);
      setCtx(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loadErr) return (
    <main className="max-w-[800px] mx-auto px-6 py-24 md:py-32" data-testid="review-error">
      <div className="label-xs text-espresso-light">Review unavailable</div>
      <h1 className="mt-4 font-serif text-4xl text-espresso">{loadErr}</h1>
      <Link to="/" className="btn-espresso mt-8 inline-block">Back to home</Link>
    </main>
  );
  if (!ctx) return <div className="max-w-[800px] mx-auto px-6 py-32 text-espresso-light label-xs">Loading…</div>;

  const remaining = ctx.items.filter((i) => !i.already_reviewed);

  if (done) {
    const stillLeft = remaining.filter((i) => i.product_id !== selectedProduct?.product_id);
    return (
      <main data-testid="review-thanks" className="max-w-[800px] mx-auto px-6 md:px-10 py-16 md:py-28 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gold text-ivory mx-auto">
          <Heart size={22} strokeWidth={1.5} />
        </div>
        <div className="mt-6 label-xs text-gold">Thank you</div>
        <h1 className="mt-4 font-serif text-5xl md:text-6xl leading-[0.95] text-espresso">
          Thank you.<br/><span className="italic text-espresso-light">Truly.</span>
        </h1>
        <p className="mt-6 text-espresso-light max-w-md mx-auto leading-relaxed">
          Your review helps other tiramisù lovers discover their next favourite. It may take a moment
          to appear on the site — every review is briefly read by our team.
        </p>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
          {stillLeft.length > 0 && (
            <button
              onClick={() => { setSelectedProduct(stillLeft[0]); resetForm(); }}
              data-testid="review-another"
              className="btn-outline"
            >
              Review another product
            </button>
          )}
          <Link to="/" className="btn-espresso" data-testid="order-again-btn">Order again</Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="review-form" className="max-w-[820px] mx-auto px-6 md:px-10 py-10 md:py-16">
      <Link to="/" className="label-xs text-espresso-light link-underline inline-flex items-center gap-1"><ChevronLeft size={14} strokeWidth={1.5} />Home</Link>

      <div className="mt-6 border-b border-line pb-8">
        <div className="label-xs text-espresso-light">How was your tiramisù?</div>
        <h1 className="mt-3 font-serif text-4xl md:text-5xl leading-[0.98] text-espresso">
          Your honest feedback<br/>
          <span className="italic text-espresso-light">helps us make every cup better.</span>
        </h1>
        <p className="mt-3 text-xs text-espresso-light">Order {ctx.order_number}</p>
      </div>

      {ctx.items.length > 1 && (
        <section className="mt-8">
          <div className="label-xs text-espresso-light mb-4">Which one?</div>
          <div className="flex flex-wrap gap-3">
            {ctx.items.map((it) => (
              <button
                key={it.product_id}
                onClick={() => !it.already_reviewed && (setSelectedProduct(it), resetForm())}
                disabled={it.already_reviewed}
                data-testid={`product-tab-${it.product_id}`}
                className={`px-4 py-2 border text-sm transition-colors ${
                  selectedProduct?.product_id === it.product_id
                    ? "border-espresso bg-espresso text-ivory"
                    : it.already_reviewed
                      ? "border-line text-espresso-light/60 line-through cursor-not-allowed"
                      : "border-line text-espresso hover:border-espresso"
                }`}
              >
                {it.product_name} {it.already_reviewed && "✓"}
              </button>
            ))}
          </div>
        </section>
      )}

      {!selectedProduct ? (
        <div className="mt-14 text-espresso-light font-serif italic text-xl">All reviewed. Thank you for every note.</div>
      ) : (
        <>
          {/* Rating */}
          <section className="mt-12" data-testid="step-rating">
            <div className="label-xs text-espresso-light">01 · Rating</div>
            <div className="mt-3 font-serif text-2xl md:text-3xl text-espresso">
              How would you rate the <span className="italic">{selectedProduct.product_name}</span>?
            </div>
            <div className="mt-6"><StarPicker value={rating} onChange={setRating} /></div>
          </section>

          {/* Text */}
          <section className="mt-12">
            <div className="label-xs text-espresso-light">02 · Tell us about it</div>
            <p className="mt-2 text-sm text-espresso-light">
              {rating >= 5 ? "Optional — but we love hearing what stood out." : "Optional. What could have been better?"}
            </p>
            <textarea
              rows={4}
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={800}
              placeholder="What did you love? How was the flavour, texture or sweetness?"
              data-testid="review-text"
              className="mt-4 w-full bg-transparent border border-line p-4 focus:outline-none focus:border-gold text-espresso"
            />
            <div className="text-right text-xs text-espresso-light mt-1">{text.length}/800</div>
          </section>

          {/* Product-specific chips */}
          <section className="mt-12 space-y-8">
            <div>
              <div className="label-xs text-espresso-light mb-3">Sweetness (optional)</div>
              <div className="flex flex-wrap gap-2">
                {SWEETNESS.map((o) => (
                  <Chip key={o.key} active={sweetness === o.key} onClick={() => setSweetness(sweetness === o.key ? "" : o.key)} testid={`sweetness-${o.key}`}>
                    {o.label}
                  </Chip>
                ))}
              </div>
            </div>
            <div>
              <div className="label-xs text-espresso-light mb-3">Texture (optional)</div>
              <div className="flex flex-wrap gap-2">
                {TEXTURE.map((o) => (
                  <Chip key={o.key} active={texture === o.key} onClick={() => setTexture(texture === o.key ? "" : o.key)} testid={`texture-${o.key}`}>
                    {o.label}
                  </Chip>
                ))}
              </div>
            </div>
            <div>
              <div className="label-xs text-espresso-light mb-3">Would you order it again?</div>
              <div className="flex flex-wrap gap-2">
                {REORDER.map((o) => (
                  <Chip key={o.key} active={reorder === o.key} onClick={() => setReorder(reorder === o.key ? "" : o.key)} testid={`reorder-${o.key}`}>
                    {o.label}
                  </Chip>
                ))}
              </div>
            </div>
          </section>

          {/* Photo */}
          <section className="mt-12">
            <div className="label-xs text-espresso-light">03 · Show us your tiramisù (optional)</div>
            <label className="mt-4 border border-dashed border-line px-5 py-8 flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-espresso transition-colors" data-testid="photo-drop">
              <input type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="photo-input" />
              {photoPreview ? (
                <img src={photoPreview} alt="Preview" className="max-h-56 object-contain" />
              ) : (
                <>
                  <Camera size={22} strokeWidth={1.5} className="text-espresso-light" />
                  <div className="font-serif text-lg text-espresso">Add a photo</div>
                  <div className="text-xs text-espresso-light">JPG / PNG / WebP · up to 10 MB. Photos appear after a quick review.</div>
                </>
              )}
            </label>
            {photo && (
              <button onClick={() => { setPhoto(null); setPhotoPreview(null); }} className="mt-3 label-xs link-underline text-espresso" data-testid="photo-remove">
                Remove photo
              </button>
            )}
          </section>

          <div className="mt-14 flex items-center justify-between border-t border-line pt-8">
            <p className="text-xs text-espresso-light italic max-w-xs">
              We publish honest feedback — the good and the awkward.
              We never trade reviews for discounts.
            </p>
            <button
              onClick={submit}
              disabled={submitting || rating < 1}
              data-testid="review-submit"
              className="btn-espresso disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? "Sending…" : "Submit review"}
            </button>
          </div>
        </>
      )}
    </main>
  );
}
